import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "../lang";
import { SITE } from "../content";
import Breadcrumb from "../components/Breadcrumb";
import BrandEyebrow from "../components/BrandEyebrow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contatti() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(lang === "it" ? "Richiesta inviata. Ti ricontattiamo entro 24h." : "Request sent. We'll reply within 24h.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(lang === "it" ? "Errore. Riprova tra qualche istante." : "Something went wrong. Please retry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="min-h-screen bg-white pt-20" data-testid="contatti-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: t.contactEyebrow }]} />
      </div>

      {/* Header */}
      <section className="pt-8 pb-4">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="mb-5"><BrandEyebrow>{t.contactEyebrow}</BrandEyebrow></div>
          <h1 className="text-[36px] md:text-[56px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {t.contactTitle}
          </h1>
          <p className="mt-4 text-[17px] md:text-[19px] text-[#1d1d1f]/80 leading-[1.55]">
            {t.contactBody}
          </p>
        </div>
      </section>

      {/* Contact info + Form */}
      <section className="section-pad">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <div>
              <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">
                {lang === "it" ? "Sede & Showroom" : "Head office & Showroom"}
              </div>
              <div className="text-[17px] text-[#1d1d1f] font-medium">{SITE.fullName}</div>
              <div className="text-[15px] text-[#1d1d1f]/85 mt-1">
                {SITE.headOffice.street}<br />
                {SITE.headOffice.zip} {SITE.headOffice.city} ({SITE.headOffice.province}) — {SITE.headOffice.country}
              </div>
            </div>

            <div>
              <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">Email</div>
              <a href={`mailto:${SITE.email}`} className="text-[17px] text-[#1D3F94] hover:underline" data-testid="contact-email">
                {SITE.email}
              </a>
            </div>

            {SITE.phone && (
              <div>
                <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">
                  {lang === "it" ? "Telefono" : "Phone"}
                </div>
                <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="text-[17px] text-[#1d1d1f]" data-testid="contact-phone">
                  {SITE.phone}
                </a>
              </div>
            )}

            {SITE.whatsapp && (
              <div>
                <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">WhatsApp Business</div>
                <a
                  href={`https://wa.me/${SITE.whatsappRaw}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[17px] text-[#1D3F94] hover:underline"
                  data-testid="contact-whatsapp"
                >
                  {SITE.whatsapp}
                </a>
              </div>
            )}

            <div>
              <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">Social</div>
              <div className="flex flex-wrap gap-4 text-[15px]">
                {SITE.instagram && (
                  <a href={SITE.instagram} target="_blank" rel="noreferrer" className="text-[#1D3F94] hover:underline" data-testid="social-instagram">
                    Instagram
                  </a>
                )}
                {SITE.facebook && (
                  <a href={SITE.facebook} target="_blank" rel="noreferrer" className="text-[#1D3F94] hover:underline" data-testid="social-facebook">Facebook</a>
                )}
                {SITE.linkedin && (
                  <a href={SITE.linkedin} target="_blank" rel="noreferrer" className="text-[#1D3F94] hover:underline">LinkedIn</a>
                )}
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={onSubmit}
            className="bg-[#f5f5f7] rounded-3xl p-6 md:p-8 space-y-4"
            data-testid="contact-form"
          >
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={lang === "it" ? "Nome e cognome" : "Full name"}
              data-testid="contact-input-name"
              className="w-full bg-white rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40 border border-[#e4e4e7]"
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email"
              data-testid="contact-input-email"
              className="w-full bg-white rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40 border border-[#e4e4e7]"
            />
            <input
              required
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder={lang === "it" ? "Telefono" : "Phone"}
              data-testid="contact-input-phone"
              className="w-full bg-white rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40 border border-[#e4e4e7]"
            />
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={lang === "it" ? "Raccontaci il tuo progetto…" : "Tell us about your project…"}
              data-testid="contact-input-message"
              className="w-full bg-white rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none resize-none focus:ring-2 focus:ring-[#1D3F94]/40 border border-[#e4e4e7]"
            />
            <div className="flex items-center justify-end pt-1">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="btn-pill btn-primary disabled:opacity-60"
              >
                {sending ? "…" : t.contactSend}
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Showroom */}
      <section className="section-pad bg-[#f5f5f7]" data-testid="contact-showrooms">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-12">
            <div className="text-[13px] uppercase tracking-widest text-[#1D3F94] mb-3">Showroom</div>
            <h2 className="text-[28px] md:text-[40px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
              {t.contactShowroomTitle}
            </h2>
          </div>

          <div className={`grid grid-cols-1 gap-4 md:gap-5 mx-auto ${SITE.showrooms.length > 1 ? "md:grid-cols-2 max-w-[1000px]" : "max-w-[560px]"}`}>
            {SITE.showrooms.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-3xl overflow-hidden border border-[#e4e4e7]"
                data-testid={`showroom-${s.id}`}
              >
                <div className="w-full h-[220px] overflow-hidden">
                  <iframe
                    title={`Showroom ${s.city}`}
                    src={s.lat && s.lon
                      ? `https://www.google.com/maps?q=${s.lat},${s.lon}&z=17&output=embed`
                      : `https://www.google.com/maps?q=${encodeURIComponent(`${s.street}, ${s.city}, ${s.country}`)}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="p-6 md:p-8">
                  <div className="text-[12px] uppercase tracking-widest text-[#6e6e73] mb-2">
                    {s.city} ({s.province})
                  </div>
                  <div className="text-[20px] font-semibold text-[#1d1d1f] mb-2">
                    Showroom {s.city}
                  </div>
                  <div className="text-[15px] text-[#1d1d1f]/85 leading-relaxed">
                    {s.street}<br />
                    {s.zip} {s.city} ({s.province}) — {s.country}
                  </div>
                  <div className="mt-3 text-[13px] text-[#6e6e73]">{s.hours}</div>
                  <a
                    href={s.lat && s.lon
                      ? `https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lon}`
                      : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${s.street}, ${s.city}, ${s.country}`)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="a-link mt-4 inline-flex"
                    data-testid={`showroom-directions-${s.id}`}
                  >
                    {lang === "it" ? "Apri in Google Maps" : "Open in Google Maps"} <span className="chev">›</span>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
